import { CardHeader, createStyles, makeStyles, Theme } from '@bb-ui/react-library';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { LearnSiteInformationItem } from './SiteInformationItem';
import { useLearnSiteContext } from 'contexts/LearnSiteContext';
import { bandMap } from 'utils';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { LearnInstanceNewRelicLink } from '@bb-learn-instances-service/common';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      marginBottom: '14px',
    },
    section: {
      marginBottom: '45px',
    },
    pageTitleContainer: {
      marginBottom: theme.spacing(4),
      display: 'flex',
      justifyContent: 'flex-end',
    },
  }),
);

export const SiteInformation: React.FC = (props) => {
  const classes = useStyles(props);
  const { t } = useTranslation();
  const { site, loading, error } = useLearnSiteContext();

  const {
    data: newRelic,
    loading: newRelicLoading,
    error: newRelicError,
  } = useRestApi<LearnInstanceNewRelicLink>(
    apiUrl('lct', `tenants/${site?.TenantId}/instances/learnInstances/newRelicLink`),
    { manual: !site?.TenantId },
  );

  const newRelicValue = () => {
    if (newRelicLoading) return t('learnSiteInformation.newRelicChecking');
    if (newRelicError) return t('learnSiteInformation.newRelicCheckFailed');
    if (newRelic?.available) return t('learnSiteInformation.newRelicOpen');

    switch (newRelic?.reason) {
      case 'NotConfigured':
        return t('learnSiteInformation.newRelicNotConfigured');
      case 'NotFound':
        return t('learnSiteInformation.newRelicNotFound');
      case 'LookupFailed':
        return t('learnSiteInformation.newRelicUnreachable');
      case 'Unsupported':
        return t('learnSiteInformation.newRelicUnsupported');
      default:
        return t('learnSiteInformation.newRelicCheckFailed');
    }
  };

  return (
    <div data-testid="learn-site-information-page">
      <div className={classes.section}>
        <CardHeader
          title={t('learnSiteInformation.pageTitle')}
          titleTypographyProps={{
            color: 'textSecondary',
            component: 'h3' as any,
            variant: 'h4',
          }}
          className={classes.heading}
        />
        {loading && <div>Loading...</div>}
        {error && <div>Error: {error.message}</div>}
        {site && (
          <div>
            <LearnSiteInformationItem
              title={t('learnSiteInformation.clientId')}
              value={site.ClientId}
              href={site.ClientId ? `/tenants/${site.ClientId}` : undefined}
              key="Captain ID"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.status')}
              value={site.Status}
              key="Status"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.fleetName')}
              value={site.FleetId}
              key="Fleet name"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.timezone')}
              value={site.Timezone}
              key="Timezone"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.installationId')}
              value={site.InstallationId}
              key="InstallationId"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.region')}
              value={site.Region}
              key="Region"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.created')}
              value={new Date(Number(site.created) * 1000).toLocaleString('en-GB').replace(',', '')}
              key="created"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.url')}
              value={site.Url}
              key="Url"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.updated')}
              value={new Date(Number(site.created) * 1000).toLocaleString('en-GB').replace(',', '')}
              key="updated"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.band')}
              value={site.Band ? (bandMap[site.Band] ?? site.Band) : 'Unknown'}
              key="band"
            />
            {site.Lite && (
              <LearnSiteInformationItem
                title={t('learnSiteInformation.learnLite')}
                value={t<string>('learnSiteInformation.learnLiteYes')}
                key="learnLite"
              />
            )}
            <LearnSiteInformationItem
              title={t('learnSiteInformation.learnVersion')}
              value={site.ActualLearnVersion || site.LearnVersion}
              key="learnVersion"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.ultraVersion')}
              value={site.ActualUltraVersion || 'Site must be running to get this value'}
              key="ultraVersion"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.tenantId')}
              value={site.LearnSiteTenantId}
              key="TenantId"
            />
            <LearnSiteInformationItem
              title={t('learnSiteInformation.newRelic')}
              value={newRelicValue()}
              href={newRelic?.available ? newRelic.url : undefined}
              key="newRelic"
            />
          </div>
        )}
      </div>
    </div>
  );
};
